import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Grid,
  Divider,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Close, Email, Phone, LocationOn } from '@mui/icons-material';

interface OrderDetailsModalProps {
  open: boolean;
  order: any | null;
  collectionEvents: any[];
  loadingDetails: boolean;
  onClose: () => void;
  onOverrideComplete: (eventId: number) => void;
  onOverrideOrderComplete: () => void;
  getBinColours: (order: any) => string[];
}

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  open,
  order,
  collectionEvents,
  loadingDetails,
  onClose,
  onOverrideComplete,
  onOverrideOrderComplete,
  getBinColours,
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Order Details #{order?.order_number}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loadingDetails ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography>Loading order details...</Typography>
          </Box>
        ) : order ? (
          <Grid container spacing={3}>
            {/* Customer Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Customer Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body1" fontWeight="medium">
                  {order.customer_first_name} {order.customer_last_name}
                </Typography>
                {order.customer_email && (
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2">{order.customer_email}</Typography>
                  </Box>
                )}
                {order.customer_phone && (
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">{order.customer_phone}</Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Collection Address */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Collection Address
              </Typography>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="body2">{order.delivery_address_1}</Typography>
                  {order.delivery_address_2 && (
                    <Typography variant="body2">{order.delivery_address_2}</Typography>
                  )}
                  <Typography variant="body2">
                    {order.delivery_city}, {order.delivery_postcode}
                  </Typography>
                  {order.delivery_country && (
                    <Typography variant="body2">{order.delivery_country}</Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Billing Address */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Billing Address
              </Typography>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="body2">{order.billing_address_1}</Typography>
                  {order.billing_address_2 && (
                    <Typography variant="body2">{order.billing_address_2}</Typography>
                  )}
                  <Typography variant="body2">
                    {order.billing_city}, {order.billing_postcode}
                  </Typography>
                  {order.billing_country && (
                    <Typography variant="body2">{order.billing_country}</Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Order Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Order Information
              </Typography>
              <Box mb={1}>
                <Typography variant="body2">
                  <strong>Order Date:</strong> {order.date_created ? new Date(order.date_created).toLocaleString() : 'N/A'}
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="body2">
                  <strong>Collection Status:</strong>{' '}
                  {(() => {
                    if (order.collection_status === 'collection_complete') {
                      return <Chip label="COMPLETED" color="success" size="small" />;
                    }
                    
                    const total = order.total_collections || collectionEvents.length || 0;
                    const completed = order.completed_collections || collectionEvents.filter(e => e.status === 'completed').length || 0;
                    const pending = order.pending_collections || collectionEvents.filter(e => e.status !== 'completed').length || 0;
                    
                    if (total === 0) {
                      return <Chip label="NO COLLECTIONS" color="default" size="small" />;
                    }
                    
                    if (completed === total) {
                      return <Chip label="COMPLETED" color="success" size="small" />;
                    }
                    
                    if (pending > 0) {
                      return (
                        <Chip 
                          label={`${pending} COLLECTION${pending > 1 ? 'S' : ''} LEFT`} 
                          color="warning" 
                          size="small" 
                        />
                      );
                    }
                    
                    return <Chip label="OVERDUE" color="error" size="small" />;
                  })()}
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="body2">
                  <strong>Total Amount:</strong> £{parseFloat(order.total).toFixed(2)}
                </Typography>
              </Box>
              {(() => {
                // Get the current driver from the latest assigned collection event
                const currentEvent = collectionEvents.find(e => 
                  e.status === 'assigned' || e.status === 'pending'
                ) || collectionEvents[0];
                const currentDriver = currentEvent?.driver_name || currentEvent?.current_driver_name;
                
                if (currentDriver) {
                  return (
                    <Box mb={1}>
                      <Typography variant="body2">
                        <strong>Current Driver:</strong> {currentDriver}
                      </Typography>
                      {currentEvent?.assigned_at && (
                        <Typography variant="body2">
                          <strong>Assigned At:</strong> {new Date(currentEvent.assigned_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                return null;
              })()}
              {order.completed_at && (
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Completed At:</strong> {new Date(order.completed_at).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Collection Events */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Collection Events
              </Typography>
              {collectionEvents.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Driver</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {collectionEvents.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            {new Date(event.scheduled_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.status.toUpperCase()}
                              size="small"
                              color={
                                event.status === 'completed' ? 'success' :
                                event.status === 'assigned' ? 'info' :
                                event.status === 'pending' ? 'warning' : 'default'
                              }
                            />
                            {event.override_completed === 1 && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                (Override)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {event.current_driver_name || event.driver_name || '-'}
                          </TableCell>
                          <TableCell>
                            {event.status !== 'completed' ? (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => onOverrideComplete(event.id)}
                              >
                                Override Complete
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Completed
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No collection events found
                </Typography>
              )}
            </Grid>

            {/* Products */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Products Ordered
              </Typography>
              <Typography variant="body2">
                {order.product_names || 'No product information available'}
              </Typography>
              
              {/* Service Details */}
              {(((order.bins_per_occasion ?? 0) > 0) ||
                ((order.bags_per_occasion ?? 0) > 0) ||
                ((order.large_bins_per_occasion ?? 0) > 0) ||
                ((order.bins_first_date ?? 0) > 0) ||
                ((order.bags_first_date ?? 0) > 0) ||
                ((order.large_bins_first_date ?? 0) > 0)) && (
                <Box mt={2}>
                  {(order.bins_per_occasion ?? 0) > 0 && (
                    <Typography variant="body2">
                      <strong>Bins:</strong> {order.bins_per_occasion} per occasion × {order.bins_number_of_occasions || 1} {(order.bins_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                    </Typography>
                  )}
                  {(order.bags_per_occasion ?? 0) > 0 && (
                    <Typography variant="body2">
                      <strong>Bags:</strong> {order.bags_per_occasion} per occasion × {order.bags_number_of_occasions || 1} {(order.bags_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                    </Typography>
                  )}
                  {(order.large_bins_per_occasion ?? 0) > 0 && (
                    <Typography variant="body2">
                      <strong>Large Bins:</strong> {order.large_bins_per_occasion} per occasion × {order.large_bins_number_of_occasions || 1} {(order.large_bins_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                    </Typography>
                  )}
                  {(order.bins_first_date ?? 0) > 0 && (
                    <Typography variant="body2">
                      <strong>Free bin (first collection date only):</strong> {order.bins_first_date}
                    </Typography>
                  )}
                  {(order.bags_first_date ?? 0) > 0 && (
                    <Typography variant="body2">
                      <strong>Free bags (first collection date only):</strong> {order.bags_first_date}
                    </Typography>
                  )}
                  {(order.large_bins_first_date ?? 0) > 0 && (
                    <Typography variant="body2">
                      <strong>Free large bin (first collection date only):</strong> {order.large_bins_first_date}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Beds & Mattresses */}
              {((order.beds_count ?? 0) > 0 || (order.mattress_count ?? 0) > 0) && (
                <Box mt={1}>
                  {(order.beds_count ?? 0) > 0 && (
                    <Typography variant="body2">
                      <strong>Beds:</strong> {order.beds_count}
                    </Typography>
                  )}
                  {(order.mattress_count ?? 0) > 0 && (
                    <Typography variant="body2">
                      <strong>Mattresses:</strong> {order.mattress_count}
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Bin Colours */}
              {(() => {
                const binColours = getBinColours(order);
                // The free / one-off bin's colour lives in first_date_bin_colours
                let firstDateColours: string[] = [];
                try {
                  const raw = order.first_date_bin_colours;
                  if (raw) firstDateColours = (typeof raw === 'string' ? JSON.parse(raw) : raw) || [];
                } catch (e) { firstDateColours = []; }
                const allColours = [...binColours, ...firstDateColours];
                if (allColours.length > 0) {
                  return (
                    <Box mt={2}>
                      <Typography variant="body2">
                        <strong>Bin Colours:</strong> {allColours.join(', ')}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}
            </Grid>

            {/* Customer Notes */}
            {order.customer_note && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Delivery Notes
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {order.customer_note}
                    </Typography>
                  </Paper>
                </Grid>
              </>
            )}
          </Grid>
        ) : null}
      </DialogContent>
      <DialogActions>
        {order && order.collection_status !== 'collection_complete' && (
          <Button
            onClick={onOverrideOrderComplete}
            color="success"
            variant="contained"
          >
            Override Order Complete
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};